import { els } from "./elements";
import {
  currentStyle, loadStyle, syncYeastUI, refreshAutoYeast, calc, setChip,
  snapshot, applySnapshot, setYeastManual, getUnitSystem, setUnitSystem, applyUnits,
} from "./controls";
import { renderHistory } from "./render";
import { fmtTempSys } from "../lib/units";
import type { UnitSystem } from "../lib/units";
import { addToHistory, loadHistory, saveHistory } from "../history/history";
import { HARD_MAX_STARTER } from "../engine";
import { encodeState } from "../share/url";
import { isSupported, enable, disable, initReacquire } from "../screen/wakelock";

export function wire(): void {
  els.style.addEventListener("change", () => {
    loadStyle(currentStyle());
    els.timeOut.textContent = els.time.value + " h";
    setChip(els.timeChips, "time", +els.time.value);
    refreshAutoYeast();
    calc();
  });

  els.yeast.addEventListener("change", () => {
    syncYeastUI();
    setYeastManual(false);
    refreshAutoYeast();
    calc();
  });
  els.sdMode.addEventListener("change", calc);
  els.starterHyd.addEventListener("input", calc);

  els.time.addEventListener("input", () => {
    els.timeOut.textContent = els.time.value + " h";
    setChip(els.timeChips, "time", +els.time.value);
    refreshAutoYeast();
    calc();
  });
  els.temp.addEventListener("input", () => {
    els.tempOut.textContent = fmtTempSys(+els.temp.value, getUnitSystem());
    setChip(els.tempChips, "temp", +els.temp.value);
    refreshAutoYeast();
    calc();
  });

  els.timeChips.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest("button"); if (!b) return;
    els.time.value = b.dataset.time!;
    els.time.dispatchEvent(new Event("input"));
  });
  els.tempChips.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest("button"); if (!b) return;
    els.temp.value = b.dataset.temp!;
    els.temp.dispatchEvent(new Event("input"));
  });

  els.yeastPct.addEventListener("input", () => {
    setYeastManual(true);
    if (els.yeast.value === "sourdough" && +els.yeastPct.value > HARD_MAX_STARTER) {
      els.yeastPct.value = String(HARD_MAX_STARTER);
    }
    calc();
  });
  els.resetYeast.addEventListener("click", () => {
    setYeastManual(false); refreshAutoYeast(); calc();
  });

  (["count", "unitWeight", "hydration", "salt", "oil", "sugar", "butter"] as const).forEach((id) => {
    els[id].addEventListener("input", calc);
  });

  els.saveBtn.addEventListener("click", () => {
    addToHistory(snapshot());
    renderHistory();
    els.saveBtn.textContent = "Saved ✓";
    els.saveBtn.classList.add("saved-ok");
    clearTimeout((els.saveBtn as HTMLButtonElement & { _t?: number })._t);
    (els.saveBtn as HTMLButtonElement & { _t?: number })._t = window.setTimeout(() => {
      els.saveBtn.textContent = "Save this recipe";
      els.saveBtn.classList.remove("saved-ok");
    }, 1400);
  });

  // Keep the URL hash in sync with state so the address is always shareable.
  let urlTimer = 0;
  const scheduleUrl = () => {
    clearTimeout(urlTimer);
    urlTimer = window.setTimeout(() => {
      history.replaceState(null, "", "#" + encodeState(snapshot()));
    }, 200);
  };

  els.historyList.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const del = target.closest<HTMLElement>("[data-del]");
    if (del) {
      saveHistory(loadHistory().filter((x) => x.id !== del.dataset.del));
      renderHistory();
      return;
    }
    const load = target.closest<HTMLElement>(".hist-load");
    if (load) {
      const snap = loadHistory().find((x) => x.id === load.dataset.id);
      if (snap) {
        applySnapshot(snap);
        scheduleUrl();
      }
    }
  });

  els.clearHistory.addEventListener("click", () => {
    saveHistory([]);
    renderHistory();
  });

  els.form.addEventListener("input", scheduleUrl);
  els.form.addEventListener("change", scheduleUrl);

  els.printBtn.addEventListener("click", () => window.print());

  let copyTimer = 0;
  els.copyLink.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(location.href);
      els.copyLink.textContent = "Copied ✓";
      clearTimeout(copyTimer);
      copyTimer = window.setTimeout(() => { els.copyLink.textContent = "Copy link"; }, 1400);
    } catch {
      /* clipboard unavailable; the live URL is still copyable manually */
    }
  });

  if (isSupported()) {
    els.wakeWrap.hidden = false;
    initReacquire();
    els.wakeToggle.addEventListener("change", () => {
      if (els.wakeToggle.checked) void enable();
      else disable();
    });
  }

  // Site-wide unit toggle (display only; device-local).
  const setUnitButtons = (sys: UnitSystem) => {
    els.unitToggle.querySelectorAll<HTMLButtonElement>("button[data-units]").forEach((b) => {
      b.setAttribute("aria-pressed", String(b.dataset.units === sys));
    });
  };
  els.unitToggle.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-units]");
    if (!b) return;
    const sys = b.dataset.units === "us" ? "us" : "metric";
    setUnitSystem(sys);
    try { localStorage.setItem("units", sys); } catch { /* private mode; ignore */ }
    setUnitButtons(sys);
    applyUnits();
  });
}
